// ADR-027 Amendment 1 §A1.2 / §A1.4 — FE side of the booking-return contract.
//
// The deployed backend (PAST-RENEWAL-001-F-BE, stripeController.js) validates
// an OPTIONAL `bookingReturn` body field on the checkout-session create call
// with a fail-closed contract: ANY invalid field => the ENTIRE object is
// rejected and success_url falls back to the bare `/success` (which would
// re-strand the paid customer — the exact R-020 vector this story closes).
//
// Therefore the FE must emit ONLY values the deployed BE will accept. These
// regexes/constants are kept byte-identical to the deployed BE
// (backend/controllers/stripeController.js @ origin/main 8f3950c):
//   BOOKING_RETURN_TO_CONSTANT = "/book-session"
//   SAFE_TOKEN_REGEX = /^[A-Za-z0-9._,()'-]{1,64}$/
//   PHOENIX_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/
//   PHOENIX_TIME_REGEX = /^([01]\d|2[0-3]):[0-5]\d$/
// If the BE contract changes, this file is the single FE coordination point.

export const BOOKING_RETURN_TO_CONSTANT = "/book-session";
const SAFE_TOKEN_REGEX = /^[A-Za-z0-9._,()'-]{1,64}$/;
const PHOENIX_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;
const PHOENIX_TIME_REGEX = /^([01]\d|2[0-3]):[0-5]\d$/;

const isSafeToken = (value) =>
  typeof value === "string" && SAFE_TOKEN_REGEX.test(value);

const isPhoenixDate = (value) =>
  typeof value === "string" && PHOENIX_DATE_REGEX.test(value);

export const isPhoenixTime = (value) =>
  typeof value === "string" && PHOENIX_TIME_REGEX.test(value);

// Build a BE-valid bookingReturn object from a raw { sessionType, date, suite, time }
// candidate, or return null if it cannot be made BE-valid. Mirrors the
// deployed BE's validateBookingReturn fail-closed semantics:
//   - returnTo is always the exact constant.
//   - sessionType + date are required and must pass the BE rules.
//   - suite is optional; a present-but-INVALID suite would fail the WHOLE
//     object closed at the BE, so we drop an invalid suite rather than send
//     it (preferring a successful round-trip without suite over a re-strand).
//   - time (Phoenix HH:mm) is optional and follows the same drop-if-invalid
//     rule as suite.
export const buildValidatedBookingReturn = ({ sessionType, date, suite, time }) => {
  if (!isSafeToken(sessionType)) return null;
  if (!isPhoenixDate(date)) return null;
  const validated = {
    returnTo: BOOKING_RETURN_TO_CONSTANT,
    sessionType,
    date,
  };
  if (suite !== undefined && suite !== null && suite !== "") {
    if (isSafeToken(suite)) {
      validated.suite = suite;
    }
    // present-but-invalid suite => omit it (BE would reject the whole object)
  }
  if (isPhoenixTime(time)) {
    validated.time = time;
  }
  return validated;
};

// Read the booking-return context off a URL query string into a BE-valid
// bookingReturn object, or null. Used by the drop-in / package choice pages
// (which receive returnTo/sessionType/date/suite/time as URL params from
// BookSession) to lift the context into the in-memory cart carrier.
export const readBookingReturnFromSearch = (search) => {
  try {
    const params = new URLSearchParams(search);
    if (params.get("returnTo") !== BOOKING_RETURN_TO_CONSTANT) return null;
    return buildValidatedBookingReturn({
      sessionType: params.get("sessionType"),
      date: params.get("date"),
      suite: params.get("suite") || undefined,
      time: params.get("time") || undefined,
    });
  } catch (err) {
    return null;
  }
};
